import { Table } from "antd"
import { motion } from "motion/react"
import { useMemo } from "react"

import EmptyState from "#blocks/EmptyState"
import Flex from "#blocks/Flex"
import PageSpinner from "#blocks/PageSpinner"
import { type TableData } from "#customTypes/types"
import {
  type ErrorType,
  formatError,
  formatErrorArray,
} from "#utils/textFormatters"

import type { ColumnsType, TableProps } from "antd/es/table"
import type { SortOrder } from "antd/es/table/interface"

/**
 * Generic, sortable table built on AntD Table.
 *
 * Sorting is CONTROLLED: the parent owns `sortField`/`sortOrder` (typically
 * persisted in userSettings) and sorts the full dataset before truncation.
 * This component injects the active order onto the matching column and reports
 * header clicks via `onSortChange`. AntD's own `sorter` comparators only affect
 * the already-truncated rows, so they must match the parent's ordering.
 *
 * Clearing a sort (AntD's third header click) reports `null`, letting the
 * parent fall back to its natural/base order.
 *
 * Rows animate individually via a custom `components.body.row`: new rows fade
 * in (leftSm-style), and `layout` slides existing rows to their new positions
 * on sort. Because `scroll.y` puts the header in its own table, the header
 * stays static -- only the body animates, so there's no full-table flicker.
 * rc-table keys each row by its rowKey, which motion uses to track layout
 * moves across reorders.
 */

// AntD passes these row props through its custom `components.body.row`. We type
// them explicitly (rather than spreading raw HTMLAttributes) so the spread onto
// motion.tr doesn't collide with motion's own onDrag/onAnimation* handlers.
type AnimatedRowProps = {
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  onClick?: React.MouseEventHandler<HTMLTableRowElement>
  onMouseEnter?: React.MouseEventHandler<HTMLTableRowElement>
  onMouseLeave?: React.MouseEventHandler<HTMLTableRowElement>
  "data-row-key"?: string
}

const AnimatedTableRow = ({ children, ...rest }: AnimatedRowProps) => (
  <motion.tr
    {...rest}
    layout
    initial={{ opacity: 0, x: 4 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, ease: "easeInOut" }}
  >
    {children}
  </motion.tr>
)

interface Table2Props<T extends TableData> {
  columns: ColumnsType<T>
  data: T[]
  onRowClick?: (id: string) => void
  isLoading?: boolean
  errors?: ErrorType | ErrorType[] | null
  emptyStateDescription?: string
  // Controlled sort
  sortField?: string
  sortOrder?: "ascend" | "descend"
  onSortChange?: (field: string, order: "ascend" | "descend" | null) => void
  // Optional row selection
  selectedRowKeys?: React.Key[]
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void
  tableProps?: Partial<TableProps<T>>
}

const Table2 = <T extends TableData>({
  columns,
  data,
  onRowClick,
  isLoading = false,
  errors,
  emptyStateDescription = "No data available",
  sortField,
  sortOrder,
  onSortChange,
  selectedRowKeys,
  onSelectionChange,
  tableProps = {},
}: Table2Props<T>) => {
  const errorText = useMemo(() => {
    if (!errors) return ""
    return Array.isArray(errors)
      ? formatErrorArray(errors)
      : formatError(errors)
  }, [errors])

  // Inject the controlled sort order onto the active column.
  const controlledColumns = useMemo<ColumnsType<T>>(() => {
    return columns.map((column) => {
      const key = "key" in column ? column.key : undefined
      const order: SortOrder = key === sortField ? (sortOrder ?? null) : null
      return { ...column, sortOrder: order }
    })
  }, [columns, sortField, sortOrder])

  const rowSelection = useMemo(() => {
    if (!onSelectionChange) return undefined
    return {
      selectedRowKeys,
      onChange: onSelectionChange,
      columnWidth: 40,
    }
  }, [selectedRowKeys, onSelectionChange])

  // Animate individual body rows; preserve any caller-provided components.
  const { components: callerComponents, ...restTableProps } = tableProps
  const components = useMemo<TableProps<T>["components"]>(
    () => ({
      ...callerComponents,
      body: {
        ...callerComponents?.body,
        row: AnimatedTableRow,
      },
    }),
    [callerComponents]
  )

  const handleChange = useMemo<NonNullable<TableProps<T>["onChange"]>>(
    () => (_pagination, _filters, sorter) => {
      if (!onSortChange) return

      const result = Array.isArray(sorter) ? sorter[0] : sorter
      const order = result?.order ?? null

      // Clear sort on third click
      if (!order) {
        onSortChange("", null)
        return
      }

      const columnKey = result?.columnKey
      if (columnKey == null) return

      onSortChange(String(columnKey), order)
    },
    [onSortChange]
  )

  if (isLoading && !data.length) {
    return (
      <Flex center style={{ height: 400, marginTop: 100 }}>
        <PageSpinner />
      </Flex>
    )
  }

  return (
    <Flex col style={{ width: "100%" }}>
      <Table<T>
        columns={controlledColumns}
        dataSource={data}
        rowKey={(record) => record.id}
        rowSelection={rowSelection}
        pagination={false}
        scroll={{ y: "calc(100vh - 260px)", x: "max-content" }}
        bordered
        size="middle"
        components={components}
        onChange={handleChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record.id),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        locale={{
          emptyText: (
            <EmptyState description={errorText || emptyStateDescription} />
          ),
        }}
        {...restTableProps}
      />
    </Flex>
  )
}

export default Table2
